/** Small helpers shared by the find → checkout → order screens. */
import { useEffect, useState } from "react";

import type { PriceChange } from "@/lib/domains";
import { formatTime } from "@/lib/format";
import { ApiError } from "@/lib/http";

/** International dialling codes for the countries offered on the address (COUNTRIES in @/lib/payments). */
export const DIAL_CODES: Record<string, string> = {
  GB: "44",
  IE: "353",
  US: "1",
  CA: "1",
  AU: "61",
  NZ: "64",
  DE: "49",
  FR: "33",
  ES: "34",
  IT: "39",
  NL: "31",
  BE: "32",
  PT: "351",
  SE: "46",
  NO: "47",
  DK: "45",
  FI: "358",
  PL: "48",
  CH: "41",
  AT: "43",
  BD: "880",
  IN: "91",
  PK: "92",
  AE: "971",
  SG: "65",
  ZA: "27",
  NG: "234",
  KE: "254",
  JP: "81",
  BR: "55",
  MX: "52",
};

/** Distinct dialling codes, shortest first ("+1", "+27", …). */
export const DIAL_OPTIONS = [...new Set(Object.values(DIAL_CODES))].sort(
  (a, b) => a.length - b.length || Number(a) - Number(b),
);

/** "SB" for "Salt & Bloom Florals" — the find page's heading tile. */
export function nameInitials(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (words.length > 1) return (words[0]![0]! + words[1]![0]!).toUpperCase();
  return (words[0] ?? "CG").slice(0, 2).toUpperCase();
}

/** The API's reason for a failed call, if it is one. */
export function reasonOf(err: unknown): string | null {
  return err instanceof ApiError ? err.reason : null;
}

/**
 * What checkout says when it stopped before charging because another purchase is already on the name (or on
 * this very payment): somebody else buying it, the customer's own earlier click, or a payment still being
 * processed. `toOrders`: the customer should look at their orders rather than pay again.
 */
export interface HoldProblem {
  tone: "warning";
  title: string;
  body: string;
  toOrders: boolean;
}

export function holdProblem(reason: string | null, message: string): HoldProblem | null {
  switch (reason) {
    case "DOMAIN_BEING_BOUGHT":
      return {
        tone: "warning",
        title: "Someone else is buying this domain right now",
        body: message,
        toOrders: false,
      };
    case "DOMAIN_ALREADY_MANAGED":
      return {
        tone: "warning",
        title: "This domain is already being bought for your workspace",
        body: message,
        toOrders: true,
      };
    case "CHECKOUT_IN_PROGRESS":
      return { tone: "warning", title: "We're still processing your payment", body: message, toOrders: true };
    default:
      return null;
  }
}

/** The names a DOMAIN_TAKEN error names (to search for alternatives). */
export function takenKeywords(err: unknown): string[] {
  if (!(err instanceof ApiError)) return [];
  const keywords = err.details.keywords;
  return Array.isArray(keywords) ? keywords.filter((k): k is string => typeof k === "string") : [];
}

/** The new total and the lines that moved, from a PRICE_CHANGED error (empty when it has none). */
export function priceChange(err: unknown): PriceChange {
  if (!(err instanceof ApiError)) return { total_cents: null, changes: [] };
  const { total_cents, changes } = err.details;
  const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
  return {
    total_cents: num(total_cents),
    changes: Array.isArray(changes)
      ? changes.flatMap((c: unknown) => {
          if (typeof c !== "object" || c === null) return [];
          const row = c as Record<string, unknown>;
          const next = num(row.new_cents);
          return typeof row.fqdn === "string" && next !== null
            ? [{ fqdn: row.fqdn, old_cents: num(row.old_cents), new_cents: next }]
            : [];
        })
      : [],
  };
}

/** Re-renders every `ms` so "taking longer than usual" can switch on without new data. */
export function useNow(ms: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

/** "10:42 am BST" */
export function clockTime(iso: string | null | undefined): string {
  return iso ? formatTime(iso) : "";
}

/** sessionStorage that never throws (private windows, blocked storage). */
export const safeSession = {
  get<T>(key: string): T | null {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  },
  set(key: string, value: unknown): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* storage unavailable — the form just won't survive a refresh */
    }
  },
  remove(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};
